import { z } from 'zod';
import { ASSET_KIND, CURRENCY } from '../types/common.js';

/**
 * Schema Product — cermin dari Drizzle table `products` (PRD §13).
 * `productSchema` = shape lengkap row DB (dipakai untuk response API).
 * `createProductInputSchema` = subset yang boleh diisi user saat create.
 * `updateProductInputSchema` = partial dari input create, untuk PATCH.
 */
export const productSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(200),
  slug: z.string().min(1).max(200),
  description: z.string().max(5000).nullable().optional(),
  price: z.number().int().nonnegative().nullable().optional(),
  currency: z.enum(CURRENCY).default('IDR'),
  category: z.string().max(100).nullable().optional(),
  brand: z.string().max(100).nullable().optional(),
  targetAudience: z.string().max(500).nullable().optional(),
  sellingPoints: z.array(z.string().min(1).max(300)).default([]),
  prohibitedClaims: z.array(z.string().min(1).max(300)).default([]),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
});
export type Product = z.infer<typeof productSchema>;

export const createProductInputSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  price: z.number().int().nonnegative().optional(),
  currency: z.enum(CURRENCY).default('IDR'),
  category: z.string().max(100).optional(),
  brand: z.string().max(100).optional(),
  targetAudience: z.string().max(500).optional(),
  sellingPoints: z.array(z.string().min(1).max(300)).max(20).default([]),
  prohibitedClaims: z.array(z.string().min(1).max(300)).max(20).default([]),
});
export type CreateProductInput = z.infer<typeof createProductInputSchema>;

export const updateProductInputSchema = createProductInputSchema.partial();
export type UpdateProductInput = z.infer<typeof updateProductInputSchema>;

/**
 * Schema ProductAsset — cermin dari Drizzle table `product_assets` (PRD §13).
 * Dipakai Fase 2 (Asset Manager, R2 upload) tapi didefinisikan sekarang
 * karena satu migration dengan `products`.
 */
export const productAssetSchema = z.object({
  id: z.string().min(1),
  productId: z.string().min(1),
  r2Key: z.string().min(1),
  kind: z.enum(ASSET_KIND),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional(),
  mimeType: z.string().max(100).nullable().optional(),
  createdAt: z.string(),
});
export type ProductAsset = z.infer<typeof productAssetSchema>;

/** Dipakai endpoint upload (Fase 2): metadata dikirim setelah file naik ke R2. */
export const registerProductAssetInputSchema = z.object({
  productId: z.string().min(1),
  r2Key: z.string().min(1),
  kind: z.enum(ASSET_KIND),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  mimeType: z.string().max(100).optional(),
});
export type RegisterProductAssetInput = z.infer<typeof registerProductAssetInputSchema>;
